<template>
  <div>
    <!-- Render weighting dropdown only when chartType is "Spectra" -->
    <div v-if="chartType === 'Spectra'">
      <label for="weight-select">Select Weighting: </label>
      <select id="weight-select" v-model="selectedWeight">
        <option value="A">A</option>
        <option value="C">C</option>
        <option value="Z">Z</option>
      </select>
      <span> Note: spectra levels before filter</span>
    </div>
    <div v-if="chartType === 'Timeseries'" class="chart-label">
      <span>Sound Pressure Levels</span>
    </div>
    <v-chart :option="chartOption" auto-resize style="width: 100%; height: 200px" />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import VChart from "vue-echarts";

// Import and register required ECharts modules.
import { use } from "echarts/core";
import { LineChart, BarChart, BoxplotChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { bankersRound } from "../helpers";

use([
  LineChart,
  BarChart,
  BoxplotChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer,
]);

export default defineComponent({
  name: "RowChart",
  components: {
    VChart,
  },
  props: {
    region: {
      type: String,
      required: true,
    },
    // Array of date ranges for the region; each range is [startTime, endTime]
    dateRanges: {
      type: Array,
      required: true,
    },
    // Array of source objects for the region; each should include: { id, name, formula, computedValues }
    sources: {
      type: Array,
      required: true,
    },
    // Measured levels: structured as { 'TOT_A': { <timestamp>: <value>, ... } }
    backendResponse: {
      type: Object,
      required: true,
    },
    // mlResponse (optional): expected to include predictions as { predictions: { 'TOT_A': { <timestamp>: <value>, ... } } }
    mlResponse: {
      type: Object,
      default: null,
    },
    chartType: {
      type: String,
      default: "Timeseries",
    },
    // New prop: weightedSpectra is expected to have keys "A", "C", or "Z"
    weightedSpectra: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedWeight: "A",
    };
  },
  methods: {
    // Format a date as "YYYY-MM-DD HH:mm:ss"
    formatDateTime(dt) {
      if (!dt) return "";
      const date = new Date(dt);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
  },
  computed: {
    // Generate xTimestamps for every date range at 10-second intervals (excluding the end time).
    xTimestamps() {
      const allTimestamps = [];
      this.dateRanges.forEach((range) => {
        if (range && range.length === 2) {
          const start = new Date(range[0]);
          const end = new Date(range[1]);
          let t = new Date(start);
          while (t < end) {
            allTimestamps.push(this.formatDateTime(t));
            t = new Date(t.getTime() + 10000); // Increment by 10 seconds
          }
        }
      });
      // Sort timestamps (zero-padded date strings sort lexicographically)
      allTimestamps.sort();
      // Remove duplicates and return unique list.
      return Array.from(new Set(allTimestamps));
    },
    // Build the full chart options reactively.
    chartOption() {
      if (this.chartType === "Spectra") {
        // Select spectra data from weightedSpectra using the selectedWeight,
        // or fallback to backendResponse.third_octaves.
        const spectraData =
          this.weightedSpectra &&
            this.weightedSpectra[this.selectedWeight] &&
            Object.keys(this.weightedSpectra[this.selectedWeight]).length
            ? this.weightedSpectra[this.selectedWeight]
            : this.backendResponse.third_octaves || {};
        // Get the frequency bands (e.g., "100Hz", "125Hz", etc.).
        const bands = Object.keys(spectraData);

        // For each band, iterate over xTimestamps, collect samples and apply the dB conversion.
        const source = [];
        bands.forEach((band) => {
          const bandData = spectraData[band] || {};
          const row = [];
          this.xTimestamps.forEach((ts) => {
            if (
              Object.prototype.hasOwnProperty.call(bandData, ts) &&
              bandData[ts] !== null &&
              bandData[ts] !== undefined
            ) {
              // Convert from dB to linear and back to dB.
              const dB = Number(bandData[ts]);
              const linear = Math.pow(10, dB / 10);
              const aggregated_dB = bankersRound(10 * Math.log10(linear));
              row.push(aggregated_dB);
            }
          });
          source.push(row);
        });

        // Determine the maximum number of samples among all bands.
        let maxSamples = 0;
        source.forEach((row) => {
          if (row.length > maxSamples) {
            maxSamples = row.length;
          }
        });
        // Pad each row with nulls if needed.
        source.forEach((row) => {
          while (row.length < maxSamples) {
            row.push(null);
          }
        });

        return {
          tooltip: {
            trigger: "item",
            axisPointer: {
              type: "shadow",
            },
            formatter: function (param) {
              // For boxplot, transformed data is a five-number array with the band name included as the first element.
              if (param.value instanceof Array && param.value.length === 6) {
                return [
                  `Band: ${param.value[0]}`,
                  `Min: ${bankersRound(param.value[1])}`,
                  `Q1: ${bankersRound(param.value[2])}`,
                  `Median: ${bankersRound(param.value[3])}`,
                  `Q3: ${bankersRound(param.value[4])}`,
                  `Max: ${bankersRound(param.value[5])}`,
                ].join("<br/>");
              }
              if (param.seriesType === "scatter") {
                return `${bankersRound(param.value[1])}`;
              }
              return param.value;
            },
          },
          // Use the dataset transform approach for boxplots.
          dataset: [
            {
              // Raw data: each row corresponds to one frequency band.
              source: source,
            },
            {
              // Transform raw data to boxplot statistics.
              transform: {
                type: "boxplot",
                config: {
                  itemNameFormatter: function (value) {
                    return bands[value.value];
                  },
                },
              },
            },
            {
              fromDatasetIndex: 1,
              fromTransformResult: 1,
            },
          ],
          xAxis: {
            type: "category",
            boundaryGap: true,
            name: "1/3rd Octave Spectra Band",
            nameGap: 30,
          },
          yAxis: {
            type: "value",
            name: "dB",
            // Sets minimum Y axis value, causes strange clipping
            // Comment out while waiting response on preferred outcome from Clayton
            // min: 0,
          },
          grid: {
            left: 30,
            top: 10,
            right: 20,
            bottom: 20,
          },
          series: [
            {
              name: "boxplot",
              type: "boxplot",
              datasetIndex: 1,
              itemStyle: {
                color: "#FFCB05",
                borderColor: "#009B69",
              },
            },
            {
              name: "outlier",
              type: "scatter",
              datasetIndex: 2,
              itemStyle: {
                color: "#B2D235",
              },
            },
          ],
        };
      }

      // For timeseries charts, use the existing logic.
      // Use a bar chart if only one timestamp exists; otherwise, use a line chart.
      const chartType = this.xTimestamps.length === 1 ? "bar" : "line";

      // Helper to combine timestamps with corresponding data values.
      const combineData = (values) =>
        this.xTimestamps.map((ts, index) => [ts, values[index]]);

      // Build the Measured series using backendResponse.TOT_A.
      const measuredValues = this.xTimestamps.map((ts) => {
        return this.backendResponse.TOT_A &&
          this.backendResponse.TOT_A[ts] !== undefined
          ? this.backendResponse.TOT_A[ts]
          : null;
      });

      const series = [];
      series.push({
        name: "Measured",
        type: chartType,
        data: combineData(measuredValues),
        showSymbol: true,
        smooth: false,
      });

      // Build the Predicted series if available from mlResponse.
      if (
        this.mlResponse &&
        this.mlResponse.predictions &&
        this.mlResponse.predictions.TOT_A
      ) {
        const predictedValues = this.xTimestamps.map((ts) => {
          return this.mlResponse.predictions.TOT_A[ts] !== undefined
            ? this.mlResponse.predictions.TOT_A[ts]
            : null;
        });
        series.push({
          name: "Predicted",
          type: chartType,
          data: combineData(predictedValues),
          showSymbol: true,
          smooth: false,
        });
      }

      // For each source, build its Calculated (computed) series.
      this.sources.forEach((source) => {
        const calculatedValues =
          source.computedValues &&
            source.computedValues.length === this.xTimestamps.length
            ? source.computedValues
            : this.xTimestamps.map(() => null);
        series.push({
          name: `${source.name} - Calculated`,
          type: chartType,
          data: combineData(calculatedValues),
          showSymbol: true,
          smooth: false,
        });
      });

      return {
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            const points = Array.isArray(params) ? params : [params];
            if (!points.length) return "";
            let tooltipHtml = `<div><strong>Time:</strong> ${points[0].data[0]}</div>`;
            points.forEach((item) => {
              const value =
                item.data[1] !== null ? item.data[1].toFixed(1) : "N/A";
              tooltipHtml += `<div>${item.marker} <strong>${item.seriesName}:</strong> ${value} dB</div>`;
            });
            return tooltipHtml;
          },
        },
        grid: {
          left: 25,
          top: 30,
          right: 5,
          bottom: 20,
        },
        legend: {
          data: series.map((s) => s.name),
        },
        xAxis: {
          type: "time",
          boundaryGap: false,
          data: this.xTimestamps,
        },
        yAxis: {
          type: "value",
          name: "dB",
        },
        series: series,
      };
    },
  },
});
</script>

<style scoped>
.chart-label {
  height: 1.25rem;
}
</style>
